import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Label,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Text,
  Textarea,
  useToast,
} from '@musa.tech/design-system';
import { ContestacaoApiError } from '@/integrations/contestacao/client';
import { useOpenContestation, useUploadContestationEvidence } from '@/hooks/useContestations';
import {
  findMotive,
  OBJECT_TYPE_LABELS,
  type ContestationMotiveGroup,
} from './contestationOptions';
import { EvidencePicker } from './EvidencePicker';

/**
 * Mensagens das recusas de abertura. O serviço devolve `409` com um código de negócio, e cada um
 * tem uma explicação diferente para o gerador — cair no texto genérico do erro deixaria a pessoa
 * sem saber o que fazer.
 */
const CONFLICT_MESSAGES: Record<string, string> = {
  contestation_outside_deadline:
    'O prazo para contestar esta medição já encerrou.',
  active_contestation_exists:
    'Este item já tem uma contestação em andamento. Acompanhe o histórico abaixo.',
  billing_cycle_not_published:
    'Esta medição ainda não foi liberada para contestação.',
  conflicting_contestation_exists:
    'Já existe uma contestação da medição inteira em andamento — não é possível contestar um item isoladamente ao mesmo tempo.',
};

function errorMessage(error: unknown): string {
  if (error instanceof ContestacaoApiError) {
    if (error.isConflict && error.code && CONFLICT_MESSAGES[error.code]) {
      return CONFLICT_MESSAGES[error.code];
    }
    if (error.isForbidden) {
      return 'Você não tem permissão para contestar este item.';
    }
    return error.message;
  }
  return 'Não foi possível registrar a contestação. Tente novamente.';
}

interface ContestationFormProps {
  /** Objeto contestado — o motivo escolhido decide se é a coleta ou o lançamento dela. */
  objectIds: {
    COLLECT?: string;
    TRANSACTION?: string | null;
    BILLING_CYCLE?: string;
  };
  motiveGroups: ContestationMotiveGroup[];
  onCancel: () => void;
  onOpened?: () => void;
}

export function ContestationForm({
  objectIds,
  motiveGroups,
  onCancel,
  onOpened,
}: ContestationFormProps) {
  const { toast } = useToast();

  const [motiveValue, setMotiveValue] = useState('');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState<File | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /**
   * Id da contestação já registrada cuja evidência não subiu. Enquanto está preenchido, o
   * formulário deixa de abrir contestação e passa a só reenviar o anexo — reabrir bateria na RN-03.
   */
  const [openedWithoutEvidence, setOpenedWithoutEvidence] = useState<string | null>(null);

  const openContestation = useOpenContestation();
  const uploadEvidence = useUploadContestationEvidence();
  const isPending = openContestation.isPending || uploadEvidence.isPending;

  const motive = findMotive(motiveGroups, motiveValue);
  const objectId = motive ? objectIds[motive.object_type] : undefined;
  const canSubmit = openedWithoutEvidence
    ? !!evidence && !evidenceError
    : !!motive && !!objectId && reason.trim().length > 0 && !evidenceError;

  const heading = motive
    ? `Detalhes da contestação — ${OBJECT_TYPE_LABELS[motive.object_type]} — ${motive.label}`
    : 'Detalhes da contestação';

  const handleFile = (file: File | null, error: string | null) => {
    setEvidence(file);
    setEvidenceError(error);
  };

  const handleRetryEvidence = async () => {
    if (!openedWithoutEvidence || !evidence) return;
    setSubmitError(null);
    try {
      await uploadEvidence.mutateAsync({ contestationId: openedWithoutEvidence, file: evidence });
      toast({
        title: 'Evidência anexada',
        description: 'O arquivo foi enviado para a contestação já registrada.',
        variant: 'success',
      });
      onOpened?.();
    } catch (error) {
      setSubmitError(errorMessage(error));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (openedWithoutEvidence) {
      await handleRetryEvidence();
      return;
    }
    if (!motive || !objectId) return;

    setSubmitError(null);
    try {
      const { contestation, evidenceError } = await openContestation.mutateAsync({
        object_type: motive.object_type,
        object_id: objectId,
        contestation_type: motive.contestation_type,
        reason: reason.trim(),
        evidence,
      });

      // A contestação valeu; só o anexo ficou pendente. Manter o formulário aberto no modo de
      // reenvio é o único caminho para não perder a prova que o gerador já escolheu.
      if (evidenceError) {
        setOpenedWithoutEvidence(contestation.id);
        setSubmitError(null);
        return;
      }

      toast({
        title: 'Contestação registrada',
        description: 'A Musa vai analisar e você será avisado do andamento.',
        variant: 'success',
      });
      onOpened?.();
    } catch (error) {
      setSubmitError(errorMessage(error));
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-neutral-200 bg-white p-4 space-y-4"
    >
      <Text variant="label" size="small" className="text-neutral-900">
        {heading}
      </Text>

      {openedWithoutEvidence && (
        <Alert variant="warning">
          <AlertDescription>
            A contestação foi registrada — só a evidência não subiu. O motivo e a descrição já estão
            salvos; reenvie o arquivo abaixo ou siga sem ele.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-1">
        <Label htmlFor="contestation-motive">
          <Text variant="label" size="small" className="text-neutral-700">
            Motivo*
          </Text>
        </Label>
        <Select value={motiveValue} onValueChange={setMotiveValue} disabled={!!openedWithoutEvidence}>
          <SelectTrigger id="contestation-motive" className="h-[35px]">
            <SelectValue placeholder="Selecione um motivo..." />
          </SelectTrigger>
          <SelectContent>
            {motiveGroups.map((group) => (
              <SelectGroup key={group.label}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.motives.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        {motive && (
          <Text variant="body" size="small" className="text-neutral-500">
            {motive.description}
          </Text>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <Label htmlFor="contestation-reason">
          <Text variant="label" size="small" className="text-neutral-700">
            Descrição*
          </Text>
        </Label>
        <Textarea
          id="contestation-reason"
          placeholder="Detalhe o que aconteceu..."
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={!!openedWithoutEvidence}
          rows={3}
        />
      </div>

      <EvidencePicker
        id="contestation-evidence"
        label={<>Evidência <span className="text-neutral-500">(opcional)</span></>}
        file={evidence}
        error={evidenceError}
        onSelect={handleFile}
      />

      {submitError && (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="tertiary"
          onClick={openedWithoutEvidence ? () => onOpened?.() : onCancel}
          disabled={isPending}
        >
          {openedWithoutEvidence ? 'Seguir sem a evidência' : 'Cancelar'}
        </Button>
        <Button type="submit" disabled={!canSubmit || isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {openedWithoutEvidence ? 'Reenviar evidência' : 'Enviar contestação'}
        </Button>
      </div>
    </form>
  );
}
